import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Box,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SidebarNavItem } from './SidebarNavItem';
import { useNavigation } from '../../../contexts/NavigationContext';
import type { NavGroup } from '../../../types/navigation';
import { transitions } from '../../../theme/tokens';

interface SidebarNavGroupProps {
  group: NavGroup;
}

export function SidebarNavGroup({ group }: SidebarNavGroupProps) {
  const { isCollapsed, expandedGroups, toggleGroup, activeSystemId } = useNavigation();
  const navigate = useNavigate();

  const isExpanded = expandedGroups.has(group.id);
  const isActive = activeSystemId === group.id;

  const handleGroupClick = () => {
    if (isCollapsed) {
      // In collapsed mode, navigate to first item
      const firstItem = group.items[0];
      if (firstItem) {
        navigate(firstItem.path);
      }
    } else {
      toggleGroup(group.id);
    }
  };

  const groupButton = (
    <ListItemButton
      onClick={handleGroupClick}
      selected={isActive && isCollapsed}
      aria-expanded={!isCollapsed ? isExpanded : undefined}
      aria-controls={!isCollapsed ? `nav-group-${group.id}` : undefined}
      sx={{
        minHeight: 44,
        px: isCollapsed ? 2.5 : 2,
        borderRadius: 1,
        mx: 1,
        mb: 0.5,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        '&.Mui-selected': {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '& .MuiListItemIcon-root': {
            color: 'inherit',
          },
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        },
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        transition: `background-color ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: isCollapsed ? 0 : 36,
          justifyContent: 'center',
          color: isActive ? 'primary.main' : 'text.secondary',
          transition: `color ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
        }}
      >
        {group.icon}
      </ListItemIcon>

      {!isCollapsed && (
        <>
          <ListItemText
            primary={group.label}
            primaryTypographyProps={{
              fontWeight: isActive ? 600 : 500,
              fontSize: '0.875rem',
              noWrap: true,
            }}
          />
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              transition: `transform ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </Box>
        </>
      )}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      {isCollapsed ? (
        <Tooltip title={group.label} placement="right" arrow>
          {groupButton}
        </Tooltip>
      ) : (
        groupButton
      )}

      {!isCollapsed && (
        <Collapse
          in={isExpanded}
          timeout={transitions.duration.normal}
          id={`nav-group-${group.id}`}
          unmountOnExit
        >
          <List component="ul" disablePadding sx={{ pl: 1 }} aria-label={`${group.label} views`}>
            {group.items.map((item) => (
              <SidebarNavItem key={item.id} item={item} />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
}
